/**
 * @file DatePicker.tsx
 * @brief Single-date picker: pill trigger + the date popover.
 *
 * @details
 * Trigger:
 *   `[ Calendar | 26 May 2026 | × ]`
 *
 * Opens `DatePopover` right under the pill (DES 01-10-2026, on the to-do's
 * Due date side sheet: "yeh bhi range picker type karo"): the range
 * picker's surface with presets on the left (Today, Tomorrow, Next Monday,
 * In a week…), one month with month and year dropdowns, and Clear / Apply.
 * Inside a Sheet or Dialog the form stays open while it is up
 * (`keepOpenWhileMenuUp` matches `data-vz-popover`).
 *
 * Stored value is an **ISO string** so URL params survive reloads.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 01-10-2026 — the range picker's popover, not a side sheet
 */

import { useState } from "react";
import { format, parseISO, startOfDay } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { DatePopover } from "@/components/ui/DateRangePopover";
import { cn } from "@/shared/theme/cn";

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = parseISO(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface DatePickerProps {
  value?: string | null;
  onChange: (next: string | undefined) => void;
  placeholder?: string;
  size?: "sm" | "md";
  disabled?: boolean;
  className?: string;
  /** Names the popover for screen readers (e.g. "Due date"). */
  title?: string;
  /** @deprecated The popover has no description line; ignored. */
  description?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  size = "md",
  disabled,
  className,
  title,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const triggerHeight = size === "sm" ? "h-9" : "h-10";
  const display = toDate(value);
  const triggerLabel = display ? format(display, "d MMM yyyy") : null;

  return (
    <DatePopover
      open={open}
      onOpenChange={setOpen}
      value={display}
      onApply={(d) => onChange(startOfDay(d).toISOString())}
      onClear={() => onChange(undefined)}
      title={title}
      anchorClassName="flex w-full"
      anchor={
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={cn(
            "group flex w-full items-center justify-between gap-2 rounded-full border border-input bg-field px-5 text-sm transition-colors",
            triggerHeight,
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !triggerLabel && "text-muted-foreground",
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <CalendarIcon className="h-4 w-4 shrink-0 opacity-60" />
            <span className="truncate">{triggerLabel ?? placeholder}</span>
          </span>
          {triggerLabel ? (
            <span
              role="button"
              aria-label="Clear date"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange(undefined);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange(undefined);
                }
              }}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : null}
        </button>
      }
    />
  );
}
